import { ASSISTANT_BILL_UPLOAD_MIME_TYPES, MAX_FILE_BYTES } from '@orn/global/assistant/billUploadConstants.js';

import { getApiBaseUrl } from '../getEnv';

/**
 * Result of a successful upload — one row from the server's `documents`
 * array. The `documentId` is what the composer threads through the
 * `attachmentDocumentIds` field on the next `user_message` WS payload;
 * `name` is the original filename (or `null` for pasted-from-clipboard /
 * unnamed uploads) used to render the chip.
 */
export interface IUploadedAttachment {
  documentId: string;
  name: string | null;
}

/**
 * Error thrown by {@link uploadAttachment}. Preserves the server-supplied
 * `error` string when present so the composer can render it inline; falls
 * back to a generic message for transport-level failures (network drop,
 * malformed JSON response). Distinct from `DOMException(AbortError)`,
 * which surfaces unchanged when the caller aborts.
 *
 * `retryAfterSeconds` is populated from the `Retry-After` header on a 429
 * response so the composer can surface "try again in N minutes" copy and
 * suppress further uploads until the bucket refills.
 */
export class UploadAttachmentError extends Error {
  readonly status: number | null;
  readonly retryAfterSeconds: number | null;

  constructor(message: string, status: number | null, retryAfterSeconds: number | null = null) {
    super(message);
    this.name = 'UploadAttachmentError';
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Local validation that mirrors the server's accept rules. Runs before the
 * network request so the composer can show an immediate inline error and
 * skip the round-trip. Returns the human-readable error string when the
 * file fails a rule, or `null` when it passes. Magic-number sniffing only
 * happens on the server — the client trusts the browser-declared MIME for
 * the preflight.
 */
export const validateAttachment = (file: File): string | null => {
  if (file.size > MAX_FILE_BYTES) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    const capMb = MAX_FILE_BYTES / (1024 * 1024);
    return `${file.name || 'File'} is ${sizeMb} MB — max is ${capMb} MB per file.`;
  }
  if (!ASSISTANT_BILL_UPLOAD_MIME_TYPES.has(file.type)) {
    return `${file.name || 'File'} type "${file.type || 'unknown'}" is not supported.`;
  }
  return null;
};

/**
 * POST one file to `/assistant/upload` and return the created
 * {@link IUploadedAttachment}. The endpoint accepts up to
 * `MAX_FILES_PER_REQUEST` files per multipart request, but this helper
 * posts one file per call so the composer's per-chip status (`uploading`
 * → `ready` / `error`) flips independently — bundling would make one
 * failed file fail the whole batch and leave the rest in limbo.
 *
 * Auth is implicit: the browser attaches the `_s` session cookie to the
 * same-origin request automatically. The endpoint also enforces
 * `FeatureGate.Assistant` — a `false` flag produces a 403 that surfaces
 * as a {@link UploadAttachmentError}.
 *
 * Throws `DOMException(AbortError)` when the caller aborts via
 * `signal.abort()`; the composer relies on that to drop the chip without
 * surfacing an error.
 */
export const uploadAttachment = async (file: File, signal: AbortSignal): Promise<IUploadedAttachment> => {
  const localError = validateAttachment(file);
  if (localError) {
    throw new UploadAttachmentError(localError, null);
  }

  const formData = new FormData();
  formData.append('files', file, file.name);

  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/assistant/upload`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    throw new UploadAttachmentError('Upload failed — check your connection and try again.', null);
  }

  if (!response.ok) {
    const serverError = await extractServerError(response);
    const retryAfterSeconds =
      response.status === 429 ? parseRetryAfterSeconds(response.headers.get('Retry-After')) : null;
    throw new UploadAttachmentError(
      serverError ?? `Upload failed (HTTP ${response.status}).`,
      response.status,
      retryAfterSeconds
    );
  }

  const body = (await response.json()) as { documents?: IUploadedAttachment[] };
  const first = body.documents?.[0];
  if (!first || typeof first.documentId !== 'string') {
    throw new UploadAttachmentError('Upload succeeded but the server response was malformed.', response.status);
  }
  return { documentId: first.documentId, name: first.name ?? null };
};

/**
 * `Retry-After` is either a non-negative integer (delta seconds, the form
 * the assistant rate-limit middleware emits today) or an HTTP-date. We
 * only need delta-seconds — fall back to `null` on anything else so the
 * caller defaults the toast to a generic "try again later" copy.
 */
const parseRetryAfterSeconds = (header: string | null): number | null => {
  if (header === null) {
    return null;
  }
  const trimmed = header.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const seconds = Number.parseInt(trimmed, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
};

const extractServerError = async (response: Response): Promise<string | null> => {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string' && body.error.length > 0) {
      return body.error;
    }
  } catch {
    // Response body was empty or not JSON — fall through to the default
    // error string. Reaching this branch usually means the request was
    // terminated by an upstream proxy (CloudFront / ALB) before the
    // handler ran, so the body is HTML or empty.
  }
  return null;
};
